import { matchQuery, Query, QueryClient, QueryKey } from '@tanstack/react-query'
import { notificationsQueries, parseUnseenObject } from '../api/notifications'
import { NNotifications } from '../interfaces/services/notifications.interfaces'

export type CountsPayload =
  NNotifications.NGetUserUnreadUnseenCount.IResponseInternal

// Lists that show per-row unread state.
const unreadKeys: QueryKey[] = [
  [{ scope: 'notification', entity: 'list' }],
  [{ scope: 'chat', entity: 'conversations' }],
  [{ scope: 'ticket', entity: 'getTickets' }],
  [{ scope: 'ticket', entity: 'getBoardTickets' }],
  [{ scope: 'ticket', entity: 'getUnreadTicketsCount' }],
]

export interface Counts {
  arrived: (
    client: QueryClient,
    payload: CountsPayload,
    userId: string | undefined
  ) => Promise<void>
  connected: (client: QueryClient) => Promise<void>
  disconnected: () => void
}

export const createCounts = (): Counts => {
  let disconnectedAt: number | undefined

  return {
    arrived: async (client, payload, userId) => {
      client.setQueryData(
        notificationsQueries.getUserUnseenUnreadCount(userId).queryKey,
        { ...payload, unseen: parseUnseenObject(payload.unseen) }
      )
      const predicate = (query: Query): boolean =>
        unreadKeys.some((queryKey) => matchQuery({ queryKey }, query))
      const inFlight = client
        .getQueryCache()
        .findAll({ predicate, fetchStatus: 'fetching' })
      await client.invalidateQueries({ predicate }, { cancelRefetch: false })
      await client.invalidateQueries(
        { predicate: (query) => inFlight.includes(query) },
        { cancelRefetch: false }
      )
    },
    connected: async (client) => {
      if (disconnectedAt === undefined) {
        return
      }
      const since = disconnectedAt
      disconnectedAt = undefined
      await client.invalidateQueries(
        { predicate: (query) => query.state.dataUpdatedAt < since },
        { cancelRefetch: false }
      )
    },
    disconnected: () => {
      disconnectedAt = Date.now()
    },
  }
}

export const counts = createCounts()
